import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  captureReferralFromSearchParams,
  getPersistedReferralPartnerCode,
} from '../../lib/referralPartner';
import {
  fetchReferralSignupOffer,
  recordReferralVisit,
  type ReferralSignupOffer,
} from '../../lib/referralPartnerOffer';
import ReferralLanding from './ReferralLanding';
import PartnerLanding from './PartnerLanding';

/**
 * Signup entry point.
 * - Standard visitors get the conversion landing with the embedded signup form.
 * - A valid active referral code (?ref= / ?partner=) gets the PARTNER landing
 *   (dark hero naming the inviting partner) which flows into the extended
 *   signup at /sign-up-new-business; the visit is logged for the funnel.
 * - Invalid/inactive codes silently get the standard version with no
 *   attribution, per product spec.
 */
export default function Signup() {
  const [searchParams] = useSearchParams();
  // Resolved on first render so we know whether to even attempt referral validation.
  const [refCode] = useState<string | null>(() => {
    const captured = captureReferralFromSearchParams(searchParams);
    return captured || getPersistedReferralPartnerCode();
  });
  const [offer, setOffer] = useState<ReferralSignupOffer | null>(null);
  const [resolving, setResolving] = useState<boolean>(!!refCode);

  useEffect(() => {
    let cancelled = false;
    if (!refCode) {
      setResolving(false);
      return;
    }
    (async () => {
      try {
        const result = await fetchReferralSignupOffer(refCode);
        if (cancelled) return;
        if (result?.ok) {
          setOffer(result);
          void recordReferralVisit(refCode); // once-per-session, best-effort
        }
      } catch {
        /* invalid/unreachable → fall back to standard signup */
      } finally {
        if (!cancelled) setResolving(false);
      }
    })();
    return () => { cancelled = true; };
  }, [refCode]);

  // Brief dark loader only while validating a referral code (avoids a flash of
  // the standard form before the landing appears).
  if (resolving) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: 'linear-gradient(135deg, #0a0a0f 0%, #111827 50%, #0a0a0f 100%)' }}
      >
        <div className="w-10 h-10 border-2 border-white/20 border-t-blue-500 rounded-full animate-spin" />
      </div>
    );
  }

  // Partner links get the dark partner landing (invited-by + extended flow);
  // everyone else gets the standard landing with the embedded form.
  if (offer?.ok) {
    return <PartnerLanding offer={offer} />;
  }
  return <ReferralLanding offer={null} />;
}
